import React from 'react';
import styles from '../styles/ReferralLandingPage.module.css';

type Props = {
  referrerFirst?: string;
  magicLink?: string;
  leadFirst?: string;
};

const ReferralLandingPage: React.FC<Props> = ({
  referrerFirst = 'Alex',
  magicLink = 'https://theiajobs.com/r/example',
  leadFirst,
}) => {
  const track = (event: string, props?: Record<string, unknown>) => {
    try {
      // @ts-expect-error analytics may not be defined in all environments
      window?.analytics?.track?.(event, props);
    } catch {}
  };

  const videoUrl = 'https://www.canva.com/design/DAGwbjbDFxc/H6YEVKnAzwocsHTWBqSdLw/watch?utm_content=DAGwbjbDFxc&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h45b42927e3';
  const calendlyUrl = 'https://calendly.com/fernando-theiajobs/theia-jobs-ai-interviewer-isa-q-a';
  const pricingUrl = 'https://www.theiajobs.com/pricing';
  const getStartedUrl = 'https://jobwizard.theiajobs.com/';

  // Personalize title with lead first name from URL (?first=Jane) or prop
  let leadFirstName = leadFirst;
  try {
    const params = new URLSearchParams(window.location.search);
    const firstFromUrl = params.get('first') || '';
    if (firstFromUrl) leadFirstName = firstFromUrl;
  } catch {}

  return (
    <div className={styles.shell}>
      <header className={styles.hero}>
        <div className={styles.heroText}>
          <div className={styles.eyebrow}>THEIA • ISA AI Interviewer</div>
          <h1 className={styles.title}>{leadFirstName ? `${leadFirstName}, are you hiring now or soon?` : 'Are you hiring now or soon?'}</h1>
          <p className={styles.subtitle}>Interview candidates 24/7 while you sleep with ISA</p>
          <ul className={styles.bullets} aria-label="Benefits">
            <li>Improve hiring while reducing cost</li>
            <li>Improve candidate experience and company brand</li>
            <li>Reduce turnover with better hiring </li>
          </ul>
          <div className={styles.ctaRow}>
          <a
            href={videoUrl}
            className={`${styles.btn} ${styles.btnPrimary}`}
            aria-label="Watch ISA in Action"
            onClick={() => track('Start Interview Clicked', { url: videoUrl })}
            target="_blank"
            rel="noopener noreferrer"
          >
            Watch ISA in Action
          </a>
          <a
            href={pricingUrl}
            className={`${styles.btn} ${styles.btnSecondary}`}
            aria-label="Pricing"
            onClick={() => track('Pricing Clicked', { url: pricingUrl })}
            rel="noopener noreferrer"
          >
            Pricing
          </a>
          </div>
          <div className={styles.testimonial}>“Personalized, convienient and interlligent.” — Candidate</div>
          <div className={styles.testimonial}>“We 5x our interview capacity without adding headcount.” — Hiring Manager</div>
        </div>
        <div className={styles.heroImageWrap}>
          <img src={process.env.PUBLIC_URL + '/ISA extending her hand.jpeg'} className={styles.heroImage} alt="ISA greets with a friendly handshake" loading="lazy" />
        </div>
      </header>


      <div className={styles.sectionAlt}>
        <img
          src={process.env.PUBLIC_URL + '/theia-logo.png'}
          alt="THEIA"
          className={styles.sectionLogo}
        />
        <section className={styles.howItWorks} aria-labelledby="how-title">
          <h2 id="how-title" className={styles.h2}>How it works</h2>
          <div className={styles.cards} role="list">
            <div className={styles.card} role="listitem"><div className={styles.cardNum} aria-hidden>1</div><div>Submit your job description</div></div>
            <div className={styles.card} role="listitem"><div className={styles.cardNum} aria-hidden>2</div><div>ISA creates a interview scorecard for you</div></div>
            <div className={styles.card} role="listitem"><div className={styles.cardNum} aria-hidden>3</div><div>ISA interview candidates and score them instantly</div></div>
          </div>
        </section>

        <section className={styles.actions} aria-label="Actions">
          <a
            href={getStartedUrl}
            className={`${styles.btn} ${styles.btnPrimary}`}
            aria-label="Get Started"
            onClick={() => track('Get Started Clicked', { url: getStartedUrl })}
            rel="noopener noreferrer"
          >
            Get Started
          </a>
          <a
            href={calendlyUrl}
            className={`${styles.btn} ${styles.btnSecondary}`}
            aria-label="Book 15‑min Q&A"
            onClick={() => track('Book Q&A Clicked', { url: calendlyUrl })}
            rel="noopener noreferrer"
          >
            Book 15‑min Q&A
          </a>
          <p className={styles.helper}>Questions? Contact <a className={styles.link} href="mailto:isa.recruiter@theiajobs.ai">isa.recruiter@theiajobs.ai</a>.</p>
        </section>
      </div>

      <section className={styles.faq} aria-labelledby="faq-title">
        <h2 id="faq-title" className={styles.h2}>FAQ</h2>
        <details className={styles.faqItem}><summary>Do you offer a free trial?</summary><p>Yes, when you book a Q&A, we can give you a free trial.</p></details>
        <details className={styles.faqItem}><summary>Do I have unlimited interviews with the AI interviewer (ISA) ?</summary><p>Yes, you can run as many interviews as you want in all the candidates</p></details>
        <details className={styles.faqItem}><summary>Does it integrate with ATS?</summary><p>Yes, it does, we can go over the integration in the Q&A.</p></details>
      </section>

      <footer className={styles.footer}>
        <p>You received this from {referrerFirst}. We’ll only reach out if you engage.</p>
        <nav className={styles.footLinks} aria-label="Legal links">
          <a className={styles.link} href="https://www.theiajobs.com/legal/privacy" rel="noopener noreferrer">Privacy</a>
          <a className={styles.link} href="https://www.theiajobs.com/legal/terms" rel="noopener noreferrer">Terms</a>
        </nav>
      </footer>

      <div className={styles.stickyCta} role="region" aria-label="Quick actions">
        <a
          href={videoUrl}
          className={`${styles.btn} ${styles.btnPrimary} ${styles.stickyPrimary}`}
          aria-label="Watch ISA in Action"
          onClick={() => track('Start Interview Clicked (Sticky)', { url: videoUrl })}
          target="_blank"
          rel="noopener noreferrer"
        >
          Watch ISA in Action
        </a>
        <a
          href={calendlyUrl}
          className={`${styles.btn} ${styles.btnSecondary}`}
          aria-label="Book 15‑min Q&A"
          onClick={() => track('Book Q&A Clicked (Sticky)', { url: calendlyUrl })}
          rel="noopener noreferrer"
        >
          Book 15‑min Q&A
        </a>
      </div>
    </div>
  );
};

export default ReferralLandingPage;
